import React, { useRef, useState } from 'react'
import { useId } from '@react-aria/utils'

import ReactPortal from 'components/Commons/ReactPortal'
import { FormattedMessage } from 'components/Commons/Translation'
import useTranslate from 'hooks/useTranslate'

import ReactSelect, { components } from 'react-select'

import { LoadingDots } from '@nfinite/react-hubstairs/components'
import Template, { LabelCappedCenter } from 'components/Commons/Inputs/Template'
import ArrowFull from './assets/arrow_full_down.svg?react'
import CloseSvg from 'assets/svg/close.svg?react'

import {
  ArrowIcon,
  LoadingWrapper,
  Message,
  Checkbox,
  SearchWrapper,
  Search,
  GroupHeadingLabel,
  OutsideArea,
  Tag,
  TopSection,
  SearchFilterToggle,
} from './styled'

import translations from './translations'

const MAX_CHAR_COUNT = 100
const MAX_HEIGHT_FULL_SIZE = '90vh'

export const MENU_PLACEMENT = {
  AUTO: 'auto',
  TOP: 'top',
  BOTTOM: 'bottom',
}

export const MENU_POSITION = {
  ABSOLUTE: 'absolute',
  FIXED: 'fixed',
}

export const DEFAULT_MENU_HEIGHT = 300

function useFullSizeProps({ ref, fullSize, onSearch }) {
  const [maxHeightMenuList, setMaxHeightMenuList] = useState(undefined)

  if (!fullSize) return

  function onMenuOpen() {
    const controlRect = ref.current?.controlRef.getBoundingClientRect()
    setMaxHeightMenuList(`calc(${MAX_HEIGHT_FULL_SIZE} - ${controlRect.y + (onSearch ? 50 : 0)}px)`)
  }

  function overrideStylesMenuList(provided, state) {
    return { ...provided, maxHeight: maxHeightMenuList }
  }

  return {
    onMenuOpen,
    overrideStylesMenuList,
  }
}

function useSearchProps({ onSearch, searchFilter }) {
  const [custom_searchInputValue, custom_searchSetInputValue] = useState('')
  const [custom_isFocusedOnSearch, custom_setIsFocusedOnSearch] = useState(false)

  if (!onSearch) return

  return {
    custom_searchInputValue,
    custom_searchSetInputValue,
    custom_isFocusedOnSearch,
    custom_setIsFocusedOnSearch,
    custom_onSearchFilter: searchFilter,
    custom_onSearch: onSearch,
  }
}

function Control({ children, ...props }) {
  const { custom_dataTestId, custom_menuPortalId } = props.selectProps
  return (
    <div data-testid={custom_dataTestId}>
      <components.Control {...props}>{children}</components.Control>
      <ReactPortal id={custom_menuPortalId} />
    </div>
  )
}

function MultiValueRemove() {
  return null
}

function MultiValue(props) {
  const { data, selectProps, removeProps } = props

  const { value, tag, tagColor = 'white' } = data

  const handleTagClick = () => {
    selectProps.onChange(
      selectProps.value.filter(option => option.value !== value),
      { action: 'remove-value' },
    )
  }

  return (
    <div {...removeProps}>
      <components.MultiValue {...props}>
        <div onClick={handleTagClick}>
          <Tag color={tagColor}>
            {tag}
            <Tag.Icon component={CloseSvg} />
          </Tag>
        </div>
      </components.MultiValue>
    </div>
  )
}

function SearchMenu({ selectProps, children }) {
  const {
    custom_searchInputValue,
    custom_searchSetInputValue,
    custom_setIsFocusedOnSearch,
    custom_onSearchFilter,
    custom_onSearch,
  } = selectProps

  const { label, status, setStatus } = custom_onSearchFilter ?? {}

  function onChange(val) {
    custom_searchSetInputValue(val)
    custom_onSearch(val)
  }

  return (
    <>
      <OutsideArea onClick={() => custom_setIsFocusedOnSearch(false)} />
      <TopSection>
        <SearchWrapper>
          <Search
            placeholder={translations.searchPlaceholder}
            maxLength={MAX_CHAR_COUNT}
            value={custom_searchInputValue}
            onChange={onChange}
            onMouseDown={e => {
              e.stopPropagation()
              e.target.focus()
            }}
            onTouchStart={e => {
              e.stopPropagation()
              e.target.focus()
            }}
            onFocus={() => custom_setIsFocusedOnSearch(true)}
            fillSpace={true}
            autoFocus
          />
        </SearchWrapper>
        {custom_onSearchFilter && (
          <SearchFilterToggle checked={status} onChange={setStatus} label={label} canClickContainer />
        )}
      </TopSection>

      {children}
    </>
  )
}

function Menu({ children, ...props }) {
  const { custom_onSearch } = props.selectProps

  return (
    <div data-testid="select_container">
      <components.Menu {...props}>
        {custom_onSearch ? <SearchMenu selectProps={props.selectProps}>{children}</SearchMenu> : children}
      </components.Menu>
    </div>
  )
}

function Option({ children, ...props }) {
  const { custom_dataTestId, value: selected, isMulti } = props.selectProps
  const { value, data } = props

  const isSelected = isMulti ? selected?.some(option => option.value === value) : selected === value

  return (
    <div data-testid={`${custom_dataTestId}-${value}`}>
      <components.Option {...props}>
        {isMulti ? <Checkbox key={value} element={data.element} checked={isSelected} iconSize="s" /> : children}
      </components.Option>
    </div>
  )
}

function GroupHeading({ children, ...props }) {
  const { custom_dataTestId } = props.selectProps
  const { value } = props
  return (
    <div data-testid={`${custom_dataTestId}-${value}`}>
      <components.GroupHeading {...props}>
        <GroupHeadingLabel>{children}</GroupHeadingLabel>
      </components.GroupHeading>
    </div>
  )
}

function DropdownIndicator({ ...props }) {
  const { isDisabled, menuIsOpen } = props.selectProps
  return isDisabled ? null : (
    <components.DropdownIndicator {...props}>
      <ArrowIcon component={ArrowFull} isOpen={menuIsOpen} />
    </components.DropdownIndicator>
  )
}

function LoadingIndicator() {
  return (
    <LoadingWrapper>
      <LoadingDots color="gray" />
    </LoadingWrapper>
  )
}

function LoadingMessage() {
  return (
    <Message>
      <FormattedMessage {...translations.loading} />
    </Message>
  )
}

function NoOptionsMessage({ selectProps }) {
  return (
    <Message>
      <FormattedMessage {...(selectProps.custom_noOptions ?? translations.noOptions)} />
    </Message>
  )
}

// TODO see if it can be removed
// data.element might be a mistake here according to the documentation - see if it can be cleaned up but it works for now
function formatOptionLabel(data, { context } = {}) {
  if (context === 'menu') {
    return data.element ?? data.label
  }

  return data.label ?? data.element
}

export function CustomSingleValue(props) {
  const { data } = props
  return <components.SingleValue {...props}>{data.label}</components.SingleValue>
}

export function CustomSingleValueWithCurrentLabel(props) {
  const { data } = props
  return <components.SingleValue {...props}>{data.currentLabel}</components.SingleValue>
}

export function PlaceholderFixedValueContainer({ children, ...props }) {
  const {
    selectProps: { placeholder },
  } = props
  // In order to have a fully working value container, we'll need to keep the render of the hidden input
  // 3rd party typed as DummyInput, in order to internally reflect the select options selected, so all
  // focus and selected value are kept and tracked accurately without breaking any component behaviour
  const selectHiddenInput = React.Children.toArray(children).find(
    input =>
      input.type.name === 'Input' ||
      input.type.name === 'DummyInput' ||
      (input.props.id?.startsWith('react-select-') && input.props.id?.endsWith('-input')),
  )

  return (
    <components.ValueContainer {...props}>
      <components.Placeholder {...props}>{placeholder}</components.Placeholder>
      {selectHiddenInput}
    </components.ValueContainer>
  )
}

/*
 * Store selected items upon selection since only the value (code/id) is persisted in the form,
 * and the items selected can disappear from the list with the search.
 *
 * items holds the displayed options (necessary for simple lists of hardcoded values)
 * initialItems holds the items for the initial values
 */
function useSelectedItems({ multiple, value, items, initialItems }) {
  const [selectedItems, setSelectedItems] = useState([])

  const data = [...initialItems, ...items, ...selectedItems]

  const selected = multiple
    ? value?.map(v => data.find(item => v === item.value))
    : data.find(item => value === item.value)

  return { selected, setSelectedItems }
}

function SelectComponent({
  items = [],
  initialItems = [], // data for initial values
  value,
  onChange,
  placeholder: placeholderIntl,
  noOptions: noOptionsIntl,
  disabled,
  loading,
  menuPlacement = MENU_PLACEMENT.AUTO,
  closeMenuOnScroll = false,
  portal = false,
  multiple = false,
  fullSize = false,
  onSearch,
  dataTestId,
  classNamePrefix = 'nfinite-select',
  onMenuScrollToBottom,
  defaultValue,
  onMenuClose,
  onMenuOpen,
  isClearable = false,
  searchFilter,
  components,
  menuShouldScrollIntoView,
  menuPosition,
}) {
  const ref = useRef()
  const selectId = useId()
  const custom_menuPortalId = `select-portal-${selectId}`

  const searchProps = useSearchProps({ onSearch, searchFilter })
  const fullSizeProps = useFullSizeProps({ ref, fullSize, onSearch })

  const translate = useTranslate()
  const placeholder = translate(placeholderIntl)

  const { selected, setSelectedItems } = useSelectedItems({ multiple, value, items, initialItems })

  function handleChange(newSelected) {
    const newValue = multiple ? newSelected.map(({ value }) => value) : (newSelected?.value ?? null)
    onChange && onChange(newValue, searchProps?.custom_searchSetInputValue)

    // persist selected items data
    if (multiple) {
      setSelectedItems(newSelected)
    } else {
      setSelectedItems(newSelected ? [newSelected] : [])
    }

    //if not multiple removes search focus
    if (!multiple) {
      searchProps?.custom_setIsFocusedOnSearch(false)
      searchProps?.custom_searchSetInputValue('')
      searchProps?.custom_onSearch('')
    }
  }

  function handleMenuOpen() {
    if (fullSizeProps) {
      fullSizeProps.onMenuOpen && fullSizeProps.onMenuOpen()
    } else {
      onMenuOpen?.()
    }
  }

  function handleMenuClose() {
    onMenuClose?.(ref)
  }

  return (
    <ReactSelect
      ref={ref}
      classNamePrefix={classNamePrefix}
      styles={{ menuList: fullSizeProps?.overrideStylesMenuList || undefined }} // override to add max-height to menu in fullSize mode
      onMenuOpen={handleMenuOpen}
      onMenuScrollToBottom={onMenuScrollToBottom}
      defaultValue={defaultValue}
      value={selected}
      onChange={handleChange}
      placeholder={placeholder}
      components={{
        Control,
        Option,
        MultiValue,
        MultiValueRemove,
        Menu,
        LoadingIndicator,
        LoadingMessage,
        NoOptionsMessage,
        DropdownIndicator,
        GroupHeading,
        ...(components ?? {}),
      }}
      formatOptionLabel={formatOptionLabel}
      hideSelectedOptions={false}
      options={items}
      menuShouldScrollIntoView={menuShouldScrollIntoView ?? !fullSize}
      closeMenuOnScroll={e => {
        // case of portal = true, to differentiate scroll on body, or on portal content
        return closeMenuOnScroll && e.target && !e.target.classList?.contains('nfinite-select__menu-list')
      }}
      onKeyDown={e => {
        // Do not select first option and/or close when space key is hit and options are available
        if (e.key === ' ' && items.length) {
          e.preventDefault()
          // Make sure to still keep default behaviour of adding a space to the search input when space key is hit
          if (searchProps?.custom_searchInputValue?.length) {
            searchProps?.custom_searchSetInputValue(searchProps?.custom_searchInputValue + ' ')
          }
        }
      }}
      closeMenuOnSelect={!multiple}
      isDisabled={disabled}
      isLoading={loading}
      isMulti={multiple}
      isClearable={isClearable}
      isSearchable={false}
      backspaceRemovesValue={false} // deactivate catching backspace - when true, it prevents using backspace on search input
      menuPlacement={menuPlacement}
      menuPosition={menuPosition}
      menuPortalTarget={portal ? document.getElementById(custom_menuPortalId) : undefined}
      custom_menuPortalId={custom_menuPortalId}
      menuIsOpen={searchProps?.custom_isFocusedOnSearch || undefined} // override to prevent closing the menu when searching
      isFocused={searchProps?.custom_isFocusedOnSearch || undefined} // override to prevent closing the menu when searching
      custom_dataTestId={dataTestId}
      custom_noOptions={noOptionsIntl}
      onMenuClose={handleMenuClose}
      {...searchProps}
    />
  )
}
function Select({ className, label: labelIntl, error, name, isRequired, disabled, layoutConfiguration, ...props }) {
  const translate = useTranslate()
  const label = translate(labelIntl)

  return (
    <Template
      name={name}
      label={label}
      error={error}
      Wrapper={LabelCappedCenter}
      layoutConfiguration={layoutConfiguration}
      isRequired={isRequired}
      disabled={disabled}
      className={className}
    >
      <SelectComponent disabled={disabled} {...props} />
    </Template>
  )
}

function LightSelect({ ...props }) {
  return <SelectComponent {...props} classNamePrefix="nfinite-select-light" />
}

Select.Light = LightSelect
export default Select
